import { useState, useCallback } from 'react'
import { message } from 'antd'
import {
  listOutfits,
  searchOutfits as searchOutfitsApi,
  filterOutfits as filterOutfitsApi,
  getOutfitStats as getOutfitStatsApi,
  getOutfitByTag,
  deleteOutfitByTag,
  generateOutfit
} from '../../../services/api/aiVideoGeneration'
import { useTaskPoller } from './useTaskPoller'
import { usePresignedUpload } from './usePresignedUpload'

/**
 * Custom hook to manage wardrobe library API calls and polling
 */
export const useWardrobe = ({ videoId } = {}) => {
  const [outfits, setOutfits] = useState([])
  const [localLoading, setLocalLoading] = useState(false)
  const [isCreatingOutfit, setIsCreatingOutfit] = useState(false)
  const [error, setError] = useState(null)

  const {
    isPolling,
    progress,
    resolveTaskData,
    stopPolling: stopTaskPolling
  } = useTaskPoller({ pollInterval: 5000 })

  const outfitCreationPoller = useTaskPoller({
    pollInterval: 3000,
    continuePollingOnError: false
  })
  const { uploadFiles } = usePresignedUpload({ videoId })

  const fetchLibrary = useCallback(
    async (params = {}) => {
      setLocalLoading(true)
      setError(null)

      try {
        const action = params.action || 'list'
        let res

        if (action === 'search') {
          res = await searchOutfitsApi({
            q: params.q || params.query,
            page: params.page || 1,
            page_size: params.page_size || params.pageSize || 20
          })
        } else if (action === 'filter') {
          res = await filterOutfitsApi({
            style: params.style,
            season: params.season,
            occasion: params.occasion,
            page: params.page || 1,
            page_size: params.page_size || params.pageSize || 20,
            sort_by: params.sort_by,
            sort_order: params.sort_order
          })
        } else if (action === 'stats') {
          res = await getOutfitStatsApi()
        } else if (action === 'get' || action === 'exists') {
          res = await getOutfitByTag(params.tag)
        } else if (action === 'delete') {
          res = await deleteOutfitByTag(params.tag)
        } else {
          res = await listOutfits({
            page: params.page || 1,
            page_size: params.page_size || params.pageSize || 20,
            sort_by: params.sort_by,
            sort_order: params.sort_order
          })
        }

        const taskResult = await resolveTaskData(res, {
          getDirectOutput: data =>
            data && data.output !== undefined ? data.output : data,
          isDirectResponse: (data, output) => {
            const status =
              typeof data?.status === 'string' ? data.status.toLowerCase() : ''
            return (
              status === 'success' ||
              data?.success === true ||
              Array.isArray(output) ||
              Array.isArray(output?.items) ||
              Array.isArray(data?.items)
            )
          }
        })

        const resultData = taskResult?.output || taskResult?.raw || {}
        const paginationData =
          taskResult?.pagination ||
          resultData?.pagination ||
          taskResult?.raw?.pagination ||
          null

        if (
          params.action === 'list' ||
          params.action === 'search' ||
          params.action === 'filter' ||
          !params.action
        ) {
          // Handle different response structures:
          // - resultData.items (when output is an object with items array)
          // - resultData itself (when output is directly the array)
          const items = Array.isArray(resultData)
            ? resultData
            : resultData?.items || []
          setOutfits(items)
          // Return both items and pagination
          return { items, pagination: paginationData }
        }

        if (params.action === 'exists') {
          return { exists: !!resultData, item: resultData }
        }

        return resultData
      } catch (err) {
        setError(err.message)
        console.error(`Wardrobe ${params.action || 'fetch'} error:`, err)
        message.error(
          err.message || `Failed to perform ${params.action || 'fetch'} action`
        )
        throw err
      } finally {
        setLocalLoading(false)
      }
    },
    [resolveTaskData, videoId]
  )

  /**
   * Create a new outfit using AI generation
   * @param {Object} params - Creation parameters
   * @param {string} params.prompt - Description of the outfit to generate
   * @param {File} [params.referenceImage] - Optional reference image file
   * @returns {Promise<Object>} - The generated outfit data
   */
  const createOutfit = useCallback(
    async ({ prompt, referenceImages = [], is_system = false }) => {
      if (!prompt || !prompt.trim()) {
        message.error('Please enter a description for the outfit')
        return { success: false, error: new Error('Prompt is required') }
      }

      setIsCreatingOutfit(true)
      setError(null)

      try {
        let referenceImageUrls = []

        // Upload reference images if provided
        if (referenceImages && referenceImages.length > 0) {
          const uploadResult = await uploadFiles({
            files: referenceImages,
            category: 'image',
            folder: 'outfit-references'
          })

          if (uploadResult.success && uploadResult.urls.length > 0) {
            referenceImageUrls = uploadResult.urls
          }
        }

        // Call generateOutfit API
        const response = await generateOutfit({
          prompt: prompt.trim(),
          reference_images: referenceImageUrls,
          video_id: videoId,
          is_system
        })

        const pollResult = await outfitCreationPoller.resolveTaskData(
          response,
          {
            getDirectOutput: data =>
              data && data.output !== undefined ? data.output : data,
            isDirectResponse: (data, output) => {
              const status =
                typeof data?.status === 'string'
                  ? data.status.toLowerCase()
                  : ''
              return status === 'success' || data?.success === true || !!output
            }
          }
        )

        const outfitData = pollResult?.output

        if (outfitData) {
          // Refresh library to include the new outfit
          fetchLibrary({ page: 1 })

          message.success('Outfit created successfully!')
          return { success: true, data: outfitData }
        }

        throw new Error('No outfit data returned')
      } catch (err) {
        setError(err.message)
        console.error('Create outfit error:', err)
        message.error(err.message || 'Failed to create outfit')
        return { success: false, error: err }
      } finally {
        setIsCreatingOutfit(false)
      }
    },
    [uploadFiles, outfitCreationPoller, fetchLibrary, videoId]
  )

  const getOutfit = useCallback(
    async tag => {
      return fetchLibrary({ action: 'get', tag })
    },
    [fetchLibrary]
  )

  const searchOutfits = useCallback(
    async query => {
      return fetchLibrary({ action: 'search', q: query })
    },
    [fetchLibrary]
  )

  const filterOutfits = useCallback(
    async filters => {
      return fetchLibrary({ action: 'filter', ...filters })
    },
    [fetchLibrary]
  )

  const getStats = useCallback(async () => {
    return fetchLibrary({ action: 'stats' })
  }, [fetchLibrary])

  const checkExists = useCallback(
    async tag => {
      try {
        const result = await fetchLibrary({ action: 'exists', tag })
        return !!result?.exists
      } catch (err) {
        return false
      }
    },
    [fetchLibrary]
  )

  const deleteOutfit = useCallback(
    async tag => {
      try {
        const res = await fetchLibrary({ action: 'delete', tag })
        message.success(`Outfit ${tag} deleted successfully`)
        // Refresh library after deletion if on library tab
        fetchLibrary()
        return res
      } catch (err) {
        // Error already handled in fetchLibrary
        throw err
      }
    },
    [fetchLibrary]
  )

  const stopPolling = useCallback(() => {
    stopTaskPolling()
    setLocalLoading(false)
  }, [stopTaskPolling])

  return {
    outfits,
    // Use localLoading for the initial fetch and wrap the polling duration
    // isPolling is only true during actual background polling
    isLoading: localLoading || isPolling,
    isCreatingOutfit,
    creationProgress: outfitCreationPoller.progress,
    progress,
    error,
    fetchLibrary,
    createOutfit,
    getOutfit,
    searchOutfits,
    filterOutfits,
    getStats,
    checkExists,
    deleteOutfit,
    stopPolling
  }
}

export default useWardrobe
